import { Immutable } from "immer";
import React from "react";
import { c, msgid, ngettext, t } from "ttag";

import { constants } from "@reactivated/constants";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { DiscountDisplayMode } from "../../models/discountDisplay";
import { isoMonths, isoWebPageURL } from "../../models/nominals";
import { ATTRIBUTE_CLASSES, CompareTileType } from "../../models/shop-compare";
import { GroupedTileModelsType } from "../../templates/ShopTileTemplate";
import { getProductPrice } from "../../utils/catalogue";
import { format } from "../../utils/format";
import { useCurrentBreakpoint } from "../../utils/hooks";
import { usePageSetting, useShouldShowPricing } from "../../utils/settings";
import {
    TILE_PRICE_OPTIONS,
    resolveTileVariant,
} from "../../utils/shopCompare";
import { sortProductOptions } from "../../utils/sorting";
import { AttributeList } from "../AttributeList";
import { ConfiguratorProductPrice } from "../ConfiguratorProductPrice";
import { Flair } from "../Flair";
import { Image } from "../Image";
import { Link } from "../Link";
import { ModelSelector } from "../ModelSelector";
import { RatingGraphic } from "../RatingGraphic";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

type ICompareTileProps = Immutable<{
    tile: CompareTileType;
    selectedProducts: number[];
    selectedSize: string;
    groupedDropdownModels: GroupedTileModelsType;
}> & {
    handleSelectProduct?: (newTile: number) => void;
};

export const CompareTile = (props: ICompareTileProps) => {
    const enableEcom = usePageSetting("enable_ecom");
    const shouldShowPricing = useShouldShowPricing();
    const currentBreakpoint = useCurrentBreakpoint();
    const tile = props.tile;
    const appSlug = usePageSetting("app_slug") || "";
    const variant = resolveTileVariant(tile, props.selectedSize);
    const variantUrl = variant?.link ?? tile.link;
    const effectivePrice = variant?.price ?? tile.price;
    const hasVariant = variant !== null;
    const totalPrice = getProductPrice(effectivePrice, TILE_PRICE_OPTIONS);
    // Following variables are necessary for i18n of the financing string
    const formattedTotalPrice = format.money(totalPrice);
    const formattedPerMonth = effectivePrice.per_month
        ? format.money(effectivePrice.per_month)
        : "";
    const financingMonths = effectivePrice.per_month_term_length
        ? isoMonths.unwrap(effectivePrice.per_month_term_length)
        : 0;
    const financingMonthsLength = financingMonths.toString();
    const financeTermsUrl = isoWebPageURL.wrap(
        "/finance-your-purchase/#terms_and_conditions",
    );
    const financingLink = (children: React.ReactNode) => (
        <Link className={styles.financingPlanLink} href={financeTermsUrl}>
            {children}
        </Link>
    );

    const sortedFeelOptions = sortProductOptions(
        "option_feel",
        tile.feel_options,
    );
    const attributeList = sortedFeelOptions.map((feelOption: string) => ({
        title: tile.feel_option_display_names?.[feelOption] ?? feelOption,
        class:
            (
                Object.keys(ATTRIBUTE_CLASSES) as Array<
                    keyof typeof ATTRIBUTE_CLASSES
                >
            ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
    }));

    const onModelSelectChange = (productId: number) => {
        props.handleSelectProduct?.(productId);
    };

    return (
        <div className={styles.container} key={JSON.stringify(tile.id)}>
            {tile.primary_flair && (
                <div className={styles.primaryFlair}>
                    <Flair flair={tile.primary_flair} />
                </div>
            )}
            <div className={styles.contentContainer}>
                <div className={styles.imageContainer}>
                    {tile.product_image?.url && (
                        <Link
                            href={variantUrl}
                            aria-label={`View ${tile.title}`}
                        >
                            <Image
                                alt={tile.product_image.title || ""}
                                src={tile.product_image.url}
                            />
                        </Link>
                    )}
                    {tile.secondary_flair && (
                        <div className={styles.secondaryFlair}>
                            <Flair flair={tile.secondary_flair} />
                        </div>
                    )}
                </div>
                <ModelSelector
                    id={`compare_tile_model_${tile.product}`}
                    label={tile.title || ""}
                    value={tile.product || null}
                    onChange={onModelSelectChange}
                    groupedModels={props.groupedDropdownModels}
                    selectedProducts={props.selectedProducts}
                />
                {appSlug !== AppSlug.TEMPURCA && (
                    <a
                        href={`${tile.product}#reviews_body`}
                        className={styles.starRating}
                    >
                        <RatingGraphic
                            cardClass={styles.starRating}
                            cardSize="large"
                            numReviews={tile.num_reviews}
                            rating={tile.rating}
                            reviewNumberClass={styles.reviewLink}
                        />
                    </a>
                )}
                {tile.description && (
                    <RichText
                        html={tile.description}
                        className={styles.description}
                    />
                )}
                <AttributeList list={attributeList} />
            </div>
            <div
                className={`${
                    currentBreakpoint.belowLaptop
                        ? styles.priceContainer
                        : styles.priceContainerHorizontal
                }`}
            >
                <div>
                    {shouldShowPricing && hasVariant ? (
                        <>
                            <ConfiguratorProductPrice
                                price={effectivePrice}
                                strikeThroughMSRP={false}
                                discountDisplayMode={
                                    DiscountDisplayMode.DISABLED
                                }
                                actualPriceStyle={"shop"}
                            />
                            {effectivePrice.per_month &&
                                effectivePrice.per_month_term_length && (
                                    <div
                                        className={`${
                                            currentBreakpoint.belowLaptop
                                                ? styles.financingPlanContainer
                                                : styles.financingPlanContainerHorizontal
                                        }`}
                                    >
                                        <span>
                                            {richT(
                                                ngettext(
                                                    msgid`or <perMonthLink>${formattedPerMonth}/mo<sup>2</sup></perMonthLink> for <monthsLink>${financingMonthsLength} month</monthsLink><sup>1</sup>`,
                                                    `or <perMonthLink>${formattedPerMonth}/mo<sup>2</sup></perMonthLink> for <monthsLink>${financingMonthsLength} months</monthsLink><sup>1</sup>`,
                                                    financingMonths,
                                                ),
                                                {
                                                    perMonthLink: (
                                                        children: React.ReactNode,
                                                    ) =>
                                                        financingLink(children),
                                                    monthsLink: (
                                                        children: React.ReactNode,
                                                    ) =>
                                                        financingLink(children),
                                                },
                                            )}
                                        </span>
                                        <br />
                                        <span>
                                            {c(
                                                "compare-tile-financing-payments",
                                            )
                                                .t`(total payments of ${formattedTotalPrice})`}
                                        </span>
                                    </div>
                                )}
                        </>
                    ) : shouldShowPricing ? (
                        <div>{t`Unavailable in this size`}</div>
                    ) : null}
                </div>
                <div className={styles.shopNowButton}>
                    {hasVariant ? (
                        <Link
                            href={variantUrl}
                            className="button al-comparisongrid-shopnow"
                        >
                            {enableEcom ? t`Shop Now` : t`Explore`}
                            <span className="ada-screenreader-only">
                                {interpolate(t`for %(productName)s`, {
                                    productName: tile.title || "",
                                })}
                            </span>
                        </Link>
                    ) : (
                        <button
                            className="button al-comparisongrid-shopnow"
                            aria-label={interpolate(
                                t`%(productName)s is unavailable in the selected size`,
                                {
                                    productName: tile.title || "",
                                },
                            )}
                            disabled
                        >
                            {t`Unavailable`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
